import { useEffect, useState } from 'react'
import { Input } from '@/components/ui'
import { useBienContext } from "../BienContext"
import SectionForm, { getFormField, useFormContext } from "../SectionForm"

const FieldList = [
  // Partie 1
  {name: "diagnostic_dpe_note", label: "DPE note"},
  {name: "diagnostic_dpe_date", label: "DPE date", type: "date"},
  {name: "diagnostic_gaz_date", label: "Gaz date", type: "date"},
  {name: "diagnostic_electricite_date", label: "Elec. date", type: "date"},
  // Partie 2
  {name: "diagnostic_plomb", label: "Plomb", type: "toggle"},
  {name: "diagnostic_plomb_date", label: "Plomb date", type: "date"},
  {name: "diagnostic_amiante", label: "Amiante", type: "toggle"},
  {name: "diagnostic_amiante_date", label: "Amiante date", type: "date"},
  // Partie 3
  {name: "zone_permis_louer", label: "Zone permis de louer", type: "toggle"},
  {name: "permis_louer", label: "Permis de louer effectué", type: "toggle"},
  {name: "permis_louer_date", label: "Permis de louer date", type: "date"},
  {name: "audit_energie", label: "Audit énergie", type: "text"}
]

const FormContent = ({fields, item}) => {
  const { control, getValues, watch, formState: { errors: formErrors } } = useFormContext()
  
  // Watchers pour déclencher la revalidation sur les dates de diagnostic
  watch('diagnostic_dpe_date')
  watch('diagnostic_gaz_date')
  watch('diagnostic_electricite_date')
  watch('diagnostic_plomb_date')
  watch('diagnostic_amiante_date')
  // Watchers pour les toggles amiante et plomb (revalidation si toggle change)
  watch('diagnostic_plomb')
  watch('diagnostic_amiante')
  
  // Helper pour créer un champ date d'échéance avec validation conditionnelle
  const createEcheanceField = (dateField, echeanceField, echeanceLabel, toggleField = null) => (
    <Input.Date
      key={echeanceField}
      control={control}
      name={echeanceField}
      label={echeanceLabel}
      className='w-full'
      rules={{
        validate: (value) => {
          const dateValue = getValues(dateField)
          // Si une date est saisie, vérifier si écheance est obligatoire
          if (!value) {
            // Si un toggleField est spécifié (amiante, plomb), vérifier s'il est positif
            if (toggleField) {
              const toggleValue = getValues(toggleField)
              // Si le toggle est false (négatif), écheance optionnelle
              if (!toggleValue) {
                return true
              }
              // Si le toggle est true (positif), écheance obligatoire
              return `${echeanceLabel} est obligatoire si le diagnostic est positif`
            }

            // Cas si pas de toggleField (autre que amiante et plomb)
            // Si pas de date, pas d'obligation d'écheance
            if (!dateValue) {
              return true
            }
            // Pour les autres diagnostics (DPE, gaz, électricité), écheance toujours obligatoire
            return `${echeanceLabel} est obligatoire si une date de diagnostic est saisie`
          }
          return true
        }
      }}
      validator={{
        hasError: (fieldName) => !!formErrors?.[fieldName],
        getErrorMessage: (fieldName) => formErrors?.[fieldName]?.message || ''
      }}
    />
  )

  return (
    <>
      <div className="flex flex-col items-start gap-6">
         <div className='w-full space-y-2'>
         {fields.slice(0,2).map( (field, index) => getFormField(control, field, index) )}
         {createEcheanceField('diagnostic_dpe_date', 'diagnostic_dpe_date_echeance', "Date d'échéance DPE")}
         <Input.File control={control}
           name="diagnostic_dpe_file"
           label="DPE fichier"
           defaultValue={item.diagnostic_dpe_file?.filename || '' }
           inputProps={{accept: ".pdf"}}
           canDelete={true}
         />
         </div>
         
         <div className='w-full space-y-2'>
         {fields.slice(2,3).map( (field, index) => getFormField(control, field, index) )}
         {createEcheanceField('diagnostic_gaz_date', 'diagnostic_gaz_date_echeance', "Date d'échéance Gaz")}
         <Input.File control={control}
           name="diagnostic_gaz_file"
           label="Gaz fichier"
           defaultValue={item.diagnostic_gaz_file?.filename || '' }
           inputProps={{accept: ".pdf"}}
           canDelete={true}
         />
         </div>

         <div className='w-full space-y-2'>
         {fields.slice(3,4).map( (field, index) => getFormField(control, field, index) )}
         {createEcheanceField('diagnostic_electricite_date', 'diagnostic_electricite_date_echeance', "Date d'échéance Électricité")}
         <Input.File control={control}
           name="diagnostic_electricite_file"
           label="Elec. fichier"
           defaultValue={item.diagnostic_electricite_file?.filename || '' }
           inputProps={{accept: ".pdf"}}
           canDelete={true}
         />
         </div>

       </div>
       <div className="flex flex-col items-start gap-6">                
        <div className='w-full space-y-2'>
          {fields.slice(4,6).map( (field, index) => getFormField(control, field, index) )}
          {createEcheanceField('diagnostic_plomb_date', 'diagnostic_plomb_date_echeance', "Date d'échéance Plomb", 'diagnostic_plomb')}
          <Input.File control={control}
            name="diagnostic_plomb_file"
            label="Plomb fichier"
            defaultValue={item.diagnostic_plomb_file?.filename || '' }
            inputProps={{accept: ".pdf"}}
            canDelete={true}
          />
          </div>

          <div className='w-full space-y-2'>
          {fields.slice(6,8).map( (field, index) => getFormField(control, field, index) )}
          {createEcheanceField('diagnostic_amiante_date', 'diagnostic_amiante_date_echeance', "Date d'échéance Amiante", 'diagnostic_amiante')}
          <Input.File control={control}
            name="diagnostic_amiante_file"
            label="Amiante fichier"
            defaultValue={item.diagnostic_amiante_file?.filename || '' }
            inputProps={{accept: ".pdf"}}
            canDelete={true}
          />
         </div>
       </div>
       <div className="flex flex-col items-start gap-4">                
         <div className='w-full space-y-2'>
           {fields.slice(8,11).map( (field, index) => getFormField(control, field, index) ) }
           <Input.File control={control}
             name="permis_louer_file"
             label="Permis de louer fichier"
             defaultValue={item.permis_louer_file?.filename || '' }
             inputProps={{accept: ".pdf"}}
             canDelete={true}
           />
         </div>

         { fields.map( f => f.name).includes("audit_energie") && 
           <div className='w-full space-y-2'>
             {fields.slice(11,12).map( (field, index) => getFormField(control, field, index) )}
             <Input.File control={control}
               name="audit_energie_file"
               label="Audit énergie fichier"
               defaultValue={item.audit_energie_file?.filename || '' }
               inputProps={{accept: ".pdf"}}
               canDelete={true}
             />

           </div>
         }
       </div>
    </>
  )
}

export default ({ title = "", onClose = () => {}, onSuccess = () => {} }) => {
  const { bien } = useBienContext()
  const typeBien = bien.type_bien
  const [fields, setFields] = useState(FieldList)

  useEffect(
    () => {
      if(typeBien !== 'immeuble') {
        setFields(FieldList.filter( f => f.name !== 'audit_energie'))
      }
    },
    [typeBien]
  )

  return (
    <SectionForm
      title={title}
      onClose={onClose}
      onSuccess={onSuccess}
      fields={[
        ...fields.map( f => f.name),
        "diagnostic_dpe_date_echeance",
        "diagnostic_gaz_date_echeance",
        "diagnostic_electricite_date_echeance",
        "diagnostic_plomb_date_echeance",
        "diagnostic_amiante_date_echeance",
        "diagnostic_dpe_file",
        "diagnostic_gaz_file",
        "diagnostic_electricite_file",
        "diagnostic_plomb_file",
        "diagnostic_amiante_file",
        "permis_louer_file",
        "audit_energie_file"
      ]}
    >
      <FormContent fields={fields} item={bien}/>
    </SectionForm>
  )
}